import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma, construirHojas, calcularProyeccionFin } from '../../servicios/programaService';
import CurvaSPrograma from './CurvaSPrograma';
import '../estilos/Programa.css';

const MS_POR_DIA = 1000 * 60 * 60 * 24;

function formatFecha(fecha) {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric',
    timeZone: 'America/Santiago'
  });
}

function diasEntre(a, b) {
  return Math.round((new Date(b) - new Date(a)) / MS_POR_DIA);
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}



// Calcula KPIs sobre actividades hoja
function calcularKPIs(hojas) {
  if (!hojas.length) return null;
  // Medianoche hora Santiago, independiente de la zona del browser
  const fechaSantiago = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const hoy = new Date(fechaSantiago + 'T00:00:00-04:00');

  // Fechas del proyecto
  let minInicio = null, maxFin = null;
  for (const a of hojas) {
    if (a.fechaInicioBase) {
      const d = new Date(a.fechaInicioBase);
      if (!minInicio || d < minInicio) minInicio = d;
    }
    if (a.fechaFinBase) {
      const d = new Date(a.fechaFinBase);
      if (!maxFin || d > maxFin) maxFin = d;
    }
  }

  const diasTotales = minInicio && maxFin ? diasEntre(minInicio, maxFin) : 0;
  const diasTranscurridos = minInicio ? Math.max(0, diasEntre(minInicio, hoy)) : 0;
  const diasRestantes = maxFin ? Math.max(0, diasEntre(hoy, maxFin)) : 0;

  let sumDur = 0, sumAvanceReal = 0, sumAvanceEsperado = 0;

  for (const a of hojas) {
    const dur = a.duracionBase || 1;
    const avReal = a.porcentajeAvance || 0;

    let avEsperado = 0;
    if (a.fechaInicioBase && a.fechaFinBase) {
      const ini = new Date(a.fechaInicioBase);
      const fin = new Date(a.fechaFinBase);
      const rango = (fin - ini) / MS_POR_DIA || 1;
      const transcurrido = (hoy - ini) / MS_POR_DIA;
      avEsperado = clamp((transcurrido / rango) * 100, 0, 100);
    }

    sumDur += dur;
    sumAvanceReal += avReal * dur;
    sumAvanceEsperado += avEsperado * dur;
  }

  const avanceReal = sumDur > 0 ? sumAvanceReal / sumDur : 0;
  const avanceEsperado = sumDur > 0 ? sumAvanceEsperado / sumDur : 0;
  const brecha = avanceEsperado - avanceReal;
  const SPI = avanceEsperado > 0 ? avanceReal / avanceEsperado : null;

  const velocidadActual = diasTranscurridos > 0 ? avanceReal / diasTranscurridos : 0;
  const velocidadNecesaria = diasRestantes > 0 ? (100 - avanceReal) / diasRestantes : null;

  const { fechaEstimada, diasRetraso } = calcularProyeccionFin(hojas, hoy);

  return {
    hoy,
    minInicio, maxFin, diasTotales, diasTranscurridos, diasRestantes,
    avanceReal: Math.round(avanceReal * 10) / 10,
    avanceEsperado: Math.round(avanceEsperado * 10) / 10,
    brecha: Math.round(brecha * 10) / 10,
    SPI: SPI ? Math.round(SPI * 100) / 100 : null,
    velocidadActual: Math.round(velocidadActual * 1000) / 1000,
    velocidadNecesaria: velocidadNecesaria ? Math.round(velocidadNecesaria * 1000) / 1000 : null,
    fechaEstimada,
    diasRetraso,
    totalHojas: hojas.length
  };
}

function calcularStats(hojas, hoy) {
  let sumDur = 0, sumReal = 0, sumEsperado = 0;
  let maxFecha = null, vencidas = 0;
  for (const a of hojas) {
    const dur = a.duracionBase || 1;
    const avReal = a.porcentajeAvance || 0;
    let avEsp = 0;
    if (a.fechaInicioBase && a.fechaFinBase) {
      const ini = new Date(a.fechaInicioBase);
      const fin = new Date(a.fechaFinBase);
      const rango = (fin - ini) / MS_POR_DIA || 1;
      avEsp = Math.min(100, Math.max(0, ((hoy - ini) / MS_POR_DIA) / rango * 100));
    }
    sumDur += dur;
    sumReal += avReal * dur;
    sumEsperado += avEsp * dur;
    if (a.fechaFinBase) {
      const d = new Date(a.fechaFinBase);
      if (!maxFecha || d > maxFecha) maxFecha = d;
      if (d < hoy && avReal < 100) vencidas++;
    }
  }
  const avanceReal     = sumDur > 0 ? Math.round((sumReal     / sumDur) * 10) / 10 : 0;
  const avanceEsperado = sumDur > 0 ? Math.round((sumEsperado / sumDur) * 10) / 10 : 0;

  const { fechaEstimada, diasRetraso } = calcularProyeccionFin(hojas, hoy);

  return { avanceReal, avanceEsperado, brecha: Math.round((avanceEsperado - avanceReal) * 10) / 10, cantHojas: hojas.length, vencidas, fechaFin: maxFecha, diasRetraso, fechaEstimada };
}

function calcularAvancePorSeccion(secciones, actividades, hojas) {
  const hoy = new Date(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' }) + 'T00:00:00-04:00'
  );

  const secMap = {};
  for (const s of secciones) {
    if (s.wbsCode) secMap[s.wbsCode] = { nombre: s.nombre, orden: s.orden };
  }
  // Mapa wbsCode → nombre para actividades resumen (nivel 2)
  const actMap = {};
  for (const a of actividades) {
    if (a.wbsCode) actMap[a.wbsCode] = a.nombre;
  }

  // Agrupar hojas por nivel1 y nivel2
  const grupos = {};
  for (const a of hojas) {
    const partes = a.wbsCode ? a.wbsCode.split('.') : ['__sin__'];
    const nivel1 = partes[0];
    const nivel2 = partes.length >= 2 ? partes.slice(0, 2).join('.') : null;

    if (!grupos[nivel1]) {
      grupos[nivel1] = { hojas: [], orden: secMap[nivel1]?.orden ?? 9999, sub: {} };
    }
    grupos[nivel1].hojas.push(a);

    if (nivel2) {
      if (!grupos[nivel1].sub[nivel2]) grupos[nivel1].sub[nivel2] = [];
      grupos[nivel1].sub[nivel2].push(a);
    }
  }

  return Object.entries(grupos)
    .sort((a, b) => a[1].orden - b[1].orden)
    .map(([nivel1, { hojas: hojasGrupo, sub }]) => {
      const stats = calcularStats(hojasGrupo, hoy);
      const subgrupos = Object.entries(sub)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .map(([nivel2, hojasSubgrupo]) => ({
          nombre: actMap[nivel2] || nivel2,
          ...calcularStats(hojasSubgrupo, hoy),
        }));
      return {
        nombre: secMap[nivel1]?.nombre || `Sección ${nivel1}`,
        ...stats,
        subgrupos,
      };
    });
}

function spiLabel(spi) {
  if (spi === null) return 'Sin datos';
  if (spi >= 1.0) return '✓ A tiempo';
  if (spi >= 0.95) return '⚡ Leve retraso';
  if (spi >= 0.80) return '⚠ Retraso moderado';
  return '✗ Retraso crítico';
}

// Color de la barra/valor de Avance Real según qué tan atrasada va la brecha (esperado-real)
function colorAvance(brecha, completada, claro = false) {
  if (completada) return claro ? '#86efac' : '#22c55e';       // verde — completado
  if (brecha > 20) return claro ? '#fca5a5' : '#ef4444';       // rojo — muy atrasado
  if (brecha > 10) return claro ? '#fdba74' : '#f97316';       // naranjo — atrasado
  return claro ? '#fde047' : '#eab308';                        // amarillo — a tiempo / leve atraso
}

function globalEsperadoEnFecha(hojas, fecha) {
  let sumDur = 0, sumEsp = 0;
  for (const a of hojas) {
    const dur = a.duracionBase || 1;
    let esp = 0;
    if (a.fechaInicioBase && a.fechaFinBase) {
      const ini = new Date(a.fechaInicioBase);
      const fin = new Date(a.fechaFinBase);
      const rango = (fin - ini) / MS_POR_DIA || 1;
      esp = clamp((fecha - ini) / MS_POR_DIA / rango * 100, 0, 100);
    }
    sumDur += dur;
    sumEsp += esp * dur;
  }
  return sumDur > 0 ? Math.round((sumEsp / sumDur) * 100) / 100 : 0;
}

function calcularPlanSemanal(hojas, hoy, secciones, actividades, avanceRealGlobal) {
  const MS_SEM = 7 * MS_POR_DIA;
  const dow = hoy.getDay();
  const diasALunes = dow === 0 ? 6 : dow - 1;
  // w=0 → semana pasada, w=1 → semana actual, w=2..7 → siguientes
  const lunesBase = new Date(hoy.getTime() - (diasALunes + 7) * MS_POR_DIA);

  // Mapas para construir la ruta jerárquica
  const secMap = {};
  for (const s of (secciones || [])) {
    if (s.wbsCode) secMap[s.wbsCode] = s.nombre;
  }
  const actMap = {};
  for (const a of (actividades || [])) {
    if (a.wbsCode) actMap[a.wbsCode] = a.nombre;
  }

  return Array.from({ length: 8 }, (_, w) => {
    const weekStart = new Date(lunesBase.getTime() + w * MS_SEM);
    const weekEnd   = new Date(weekStart.getTime() + 6 * MS_POR_DIA);
    const esAnterior = w === 0;
    const esActual   = w === 1;
    const esFutura   = w > 1;
    const refCompar  = esActual ? hoy : weekEnd;

    const activas = hojas
      .filter(a => {
        if (!a.fechaInicioBase || !a.fechaFinBase) return false;
        const ini = new Date(a.fechaInicioBase);
        const fin = new Date(a.fechaFinBase);
        if (ini <= weekEnd && fin >= weekStart) return true; // solape normal
        // Arrastre: pendiente y ya vencida antes de esta semana
        const pendiente = (a.porcentajeAvance || 0) < 100;
        return w >= 1 && pendiente && fin < weekStart;
      })
      .map(a => {
        const ini = new Date(a.fechaInicioBase);
        const fin = new Date(a.fechaFinBase);
        const dur = Math.max(1, (fin - ini) / MS_POR_DIA);
        const terminaEstaSemana = fin >= weekStart && fin <= weekEnd;
        const arrastrada = fin < weekStart;
        const diasAtraso = fin < hoy ? Math.round((hoy - fin) / MS_POR_DIA) : 0;
        const esperadoFinSem = Math.round(Math.min(100, Math.max(0, (weekEnd  - ini) / dur * 100)));
        const esperadoRef    = Math.round(Math.min(100, Math.max(0, (refCompar - ini) / dur * 100)));
        const actual = a.porcentajeAvance || 0;
        const completada = actual >= 100;
        const origenSemana = ini < weekStart ? 'anterior' : 'actual';
        const partes = a.wbsCode ? a.wbsCode.split('.') : [];
        const rutaPartes = [];
        for (let i = 1; i < partes.length; i++) {
          const code = partes.slice(0, i).join('.');
          const nombre = i === 1 ? (secMap[code] || actMap[code]) : actMap[code];
          if (nombre) rutaPartes.push(nombre);
        }
        const ruta = rutaPartes.join(' › ');
        return { nombre: a.nombre, terminaEstaSemana, arrastrada, diasAtraso, esperadoFinSem, esperadoRef, actual, completada, brecha: esperadoRef - actual, origenSemana, ruta };
      });

    const pendientes   = activas.filter(a => !a.completada)
      .sort((a, b) => b.diasAtraso - a.diasAtraso || b.brecha - a.brecha);
    const completadas  = activas.filter(a => a.completada);

    // Partidas con fecha de más atraso primero, hasta las de menor atraso; completadas al final
    const top15 = [...pendientes, ...completadas];

    // KPI agregado solo sobre pendientes
    const kpiEsperado = pendientes.length ? Math.round(pendientes.reduce((s, a) => s + a.esperadoRef, 0) / pendientes.length) : 0;
    const kpiActual   = pendientes.length ? Math.round(pendientes.reduce((s, a) => s + a.actual,       0) / pendientes.length) : 0;
    const excelenteSemana = !esFutura && pendientes.length > 0 && kpiActual >= kpiEsperado;

    const globalEspIni = globalEsperadoEnFecha(hojas, weekStart);
    const globalEspFin = globalEsperadoEnFecha(hojas, weekEnd);
    const deltaGlobalEsperado = Math.round((globalEspFin - globalEspIni) * 100) / 100;
    const deltaGlobalReal = esActual
      ? Math.round((avanceRealGlobal - globalEspIni) * 100) / 100
      : null;

    const fmt = d => d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', timeZone: 'America/Santiago' });
    const label = `${fmt(weekStart)} – ${fmt(weekEnd)}`;

    return { w, esAnterior, esActual, esFutura, label, partidas: top15, totalActivas: activas.length, totalCompletadas: completadas.length, kpiEsperado, kpiActual, excelenteSemana, deltaGlobalEsperado, deltaGlobalReal };
  });
}

export default function DashboardPrograma() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [semanaSeleccionada, setSemanaSeleccionada] = useState(1);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const prog = await obtenerPrograma();
      setDatos(prog);
    } catch {
      setError('Error al cargar el programa');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const hojas = useMemo(() => construirHojas(datos.actividades), [datos.actividades]);
  const kpi = useMemo(() => calcularKPIs(hojas), [hojas]);
  const avancePorSeccion = useMemo(() => calcularAvancePorSeccion(datos.secciones, datos.actividades, hojas), [datos.secciones, datos.actividades, hojas]);
  const planSemanal = useMemo(() => kpi ? calcularPlanSemanal(hojas, kpi.hoy, datos.secciones, datos.actividades, kpi.avanceReal) : [], [hojas, kpi, datos.secciones, datos.actividades]);


  if (cargando) {
    return (
      <div className="programa-container">
        <div className="programa-header">
          <div className="programa-skeleton-block" style={{ width: 260, height: 26 }} />
        </div>
        <div className="db-grid">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="db-card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div className="programa-skeleton-block" style={{ width: '55%', height: 34, marginBottom: 10 }} />
              <div className="programa-skeleton-block" style={{ width: '80%', height: 13, marginBottom: 6 }} />
              <div className="programa-skeleton-block" style={{ width: '45%', height: 11 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!datos.programa) {
    return (
      <div className="programa-container">
        <div className="programa-empty">
          <h3>Sin programa importado</h3>
          <p>Ve a la tab <strong>Programa</strong> e importa un XML para ver el Dashboard.</p>
        </div>
      </div>
    );
  }

  if (!kpi) {
    return (
      <div className="programa-container">
        <div className="programa-empty"><p>Sin actividades para calcular KPIs.</p></div>
      </div>
    );
  }

  const spi = kpi.SPI;

  return (
    <div className="programa-container">
      {/* Header */}
      <div className="programa-header">
        <div>
          <h2>Dashboard · Programa de Obra</h2>
          <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: 2 }}>
            {datos.programa.nombre} · Hoy: {formatFecha(kpi.hoy)}
            &nbsp;·&nbsp; {kpi.diasTranscurridos}d transcurridos · {kpi.diasRestantes}d restantes
          </div>
        </div>
      </div>

      {error && (
        <div className="programa-error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      {/* ── Curva S ── */}
      <CurvaSPrograma hojas={hojas} kpi={kpi} />

      {/* ── Franja KPI compacta ── */}
      <div className="db-kpi-strip">
        <div className="db-kpi-strip-item">
          <div className="db-kpi-strip-val" style={{ color: '#f97316' }}>{kpi.avanceReal}%</div>
          <div className="db-kpi-strip-lbl">Avance real · {kpi.totalHojas} partidas</div>
        </div>
        <div className="db-kpi-strip-item">
          <div className="db-kpi-strip-val" style={{ color: '#3b82f6' }}>{kpi.avanceEsperado}%</div>
          <div className="db-kpi-strip-lbl">Esperado según base</div>
        </div>
        <div className="db-kpi-strip-item">
          <div className="db-kpi-strip-val" style={{ color: spi === null ? '#94a3b8' : spi >= 0.95 ? '#16a34a' : spi >= 0.80 ? '#ca8a04' : '#dc2626' }}>
            {spi !== null ? spi.toFixed(2) : '—'}
          </div>
          <div className="db-kpi-strip-lbl">Índice de avance (IDC) · {spiLabel(spi)}</div>
        </div>
        <div className="db-kpi-strip-item">
          <div className="db-kpi-strip-val" style={{ fontSize: '1.1rem', color: kpi.diasRetraso !== null && kpi.diasRetraso > 0 ? '#dc2626' : '#16a34a' }}>
            {kpi.fechaEstimada ? formatFecha(kpi.fechaEstimada) : '—'}
          </div>
          <div className="db-kpi-strip-lbl">
            Término estimado según atrasos
            {kpi.diasRetraso !== null && (
              <span style={{ marginLeft: 6, fontWeight: 700, color: kpi.diasRetraso > 0 ? '#dc2626' : '#16a34a' }}>
                ({kpi.diasRetraso > 0 ? `+${kpi.diasRetraso}d` : `${kpi.diasRetraso}d`})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Avance por Sección ── */}
      {avancePorSeccion.length > 0 && (
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Avance por Sección
          </h3>
          <div className="programa-tabla-wrapper">
            <table className="programa-tabla">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>#</th>
                  <th>Sección / Grupo</th>
                  <th style={{ width: 200 }}>Avance Real</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Real</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Esperado</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Brecha</th>
                  <th style={{ width: 110, textAlign: 'center' }}>Término Esperado</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Días Atraso</th>
                  <th style={{ width: 110, textAlign: 'center' }}>Término Real</th>
                  <th style={{ width: 75, textAlign: 'center' }}>Vencidas</th>
                </tr>
              </thead>
              <tbody>
                {avancePorSeccion.map((sec, i) => {
                  const completadaSec = sec.avanceReal >= 100;
                  const colorSec = colorAvance(sec.brecha, completadaSec);
                  return (
                    <React.Fragment key={`grupo-${i}`}>
                      {/* Fila nivel 1 — sección */}
                      <tr key={`sec-${i}`} style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                        <td style={{ color: '#94a3b8', fontSize: '0.8rem', paddingLeft: 12 }}>{i + 1}</td>
                        <td style={{ paddingLeft: 12, fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>{sec.nombre}</td>
                        <td style={{ paddingRight: 12 }}>
                          <div className="programa-barra-track">
                            <div className="programa-barra-fill" style={{
                              width: `${sec.avanceReal}%`,
                              background: colorSec
                            }} />
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem',
                          color: colorSec }}>
                          {sec.avanceReal}%
                        </td>
                        <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>{sec.avanceEsperado}%</td>
                        <td style={{ textAlign: 'center' }}>
                          {sec.brecha > 0
                            ? <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.85rem' }}>▼ {sec.brecha}%</span>
                            : <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>▲ {Math.abs(sec.brecha)}%</span>
                          }
                        </td>
                        <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>{formatFecha(sec.fechaFin)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {sec.avanceReal >= 100
                            ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>✓ Completado</span>
                            : sec.diasRetraso === null
                              ? <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                              : <span style={{ color: sec.diasRetraso > 0 ? '#dc2626' : '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>
                                  {sec.diasRetraso > 0 ? `-${sec.diasRetraso}d` : `+${Math.abs(sec.diasRetraso)}d`}
                                </span>
                          }
                        </td>
                        <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>
                          {sec.avanceReal >= 100
                            ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>✓</span>
                            : sec.fechaEstimada ? formatFecha(sec.fechaEstimada) : '—'
                          }
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {sec.vencidas > 0
                            ? <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.85rem' }}>{sec.vencidas}</span>
                            : <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>}
                        </td>
                      </tr>

                      {/* Filas nivel 2 — sub-grupos */}
                      {sec.subgrupos.map((sub, j) => {
                        const completadaSub = sub.avanceReal >= 100;
                        const colorSub = colorAvance(sub.brecha, completadaSub, true);
                        return (
                          <tr key={`sub-${i}-${j}`} className="programa-fila-actividad">
                            <td style={{ color: '#cbd5e1', fontSize: '0.75rem', paddingLeft: 12 }}></td>
                            <td style={{ paddingLeft: 28, color: '#475569', fontSize: '0.85rem' }}>
                              <span style={{ color: '#94a3b8', marginRight: 6 }}>└</span>
                              {sub.nombre}
                            </td>
                            <td style={{ paddingRight: 12 }}>
                              <div className="programa-barra-track" style={{ height: 6 }}>
                                <div className="programa-barra-fill" style={{
                                  width: `${sub.avanceReal}%`,
                                  background: colorSub
                                }} />
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.85rem',
                              color: colorSub }}>
                              {sub.avanceReal}%
                            </td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{sub.avanceEsperado}%</td>
                            <td style={{ textAlign: 'center' }}>
                              {sub.brecha > 0
                                ? <span style={{ color: '#f87171', fontWeight: 600, fontSize: '0.82rem' }}>▼ {sub.brecha}%</span>
                                : <span style={{ color: '#4ade80', fontWeight: 600, fontSize: '0.82rem' }}>▲ {Math.abs(sub.brecha)}%</span>
                              }
                            </td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>{formatFecha(sub.fechaFin)}</td>
                            <td style={{ textAlign: 'center' }}>
                              {sub.avanceReal >= 100
                                ? <span style={{ color: '#4ade80', fontWeight: 600, fontSize: '0.82rem' }}>✓ Completado</span>
                                : sub.diasRetraso === null
                                  ? <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>—</span>
                                  : <span style={{ color: sub.diasRetraso > 0 ? '#f87171' : '#4ade80', fontWeight: 600, fontSize: '0.82rem' }}>
                                      {sub.diasRetraso > 0 ? `-${sub.diasRetraso}d` : `+${Math.abs(sub.diasRetraso)}d`}
                                    </span>
                              }
                            </td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.80rem' }}>
                              {sub.avanceReal >= 100
                                ? <span style={{ color: '#4ade80', fontWeight: 600 }}>✓</span>
                                : sub.fechaEstimada ? formatFecha(sub.fechaEstimada) : '—'
                              }
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {sub.vencidas > 0
                                ? <span style={{ color: '#f87171', fontWeight: 600, fontSize: '0.82rem' }}>{sub.vencidas}</span>
                                : <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Plan de Ejecución Semanal ── */}
      {planSemanal.length > 0 && (() => {
        const sem = planSemanal[semanaSeleccionada] || planSemanal[1];
        return (
          <div style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                Plan de Ejecución Semanal
              </h3>
              {/* Selector de semana */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {planSemanal.map(s => (
                  <button
                    key={s.w}
                    onClick={() => setSemanaSeleccionada(s.w)}
                    style={{
                      padding: '4px 10px', fontSize: '0.75rem', fontWeight: s.w === semanaSeleccionada ? 700 : 400,
                      border: `1.5px solid ${s.w === semanaSeleccionada ? '#111827' : '#e2e8f0'}`,
                      borderRadius: 6, cursor: 'pointer',
                      background: s.w === semanaSeleccionada ? '#111827' : '#fff',
                      color: s.w === semanaSeleccionada ? '#ffffff' : '#6b7280',
                    }}
                  >
                    {s.esAnterior ? 'Ant.' : s.esActual ? 'Actual' : `+${s.w - 1}`}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)' }}>
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                background: '#fff',
                borderBottom: '1px solid #e2e8f0',
              }}>
                {/* Título + badge estado */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                      {sem.esAnterior ? '← Semana pasada' : sem.esActual ? '● Semana actual' : `Semana +${sem.w - 1}`}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{sem.label}</span>
                  </div>
                  {sem.excelenteSemana && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '3px 12px', borderRadius: 99 }}>
                      ✓ Excelente · mejor de lo esperado
                    </span>
                  )}
                  {!sem.esFutura && !sem.excelenteSemana && sem.totalActivas > 0 && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '3px 12px', borderRadius: 99 }}>
                      Partidas: esperado {sem.kpiEsperado}% · real {sem.kpiActual}%
                    </span>
                  )}
                  {sem.esFutura && sem.totalActivas > 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', background: '#f1f5f9', padding: '3px 12px', borderRadius: 99 }}>
                      Meta partidas: {sem.kpiEsperado}%
                    </span>
                  )}
                </div>

                {/* Cuadrícula KPI */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sem.deltaGlobalReal !== null ? 4 : 3}, 1fr)`, gap: 10 }}>
                  {/* Δ Meta global */}
                  <div style={{ background: '#fff', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Meta global semana</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1d4ed8', lineHeight: 1 }}>
                      +{sem.deltaGlobalEsperado}%
                    </div>
                    <div style={{ fontSize: '0.71rem', color: '#94a3b8', marginTop: 4 }}>avance esperado del proyecto</div>
                  </div>

                  {/* Real avanzado — solo semana actual */}
                  {sem.deltaGlobalReal !== null && (() => {
                    const adelanto = sem.deltaGlobalReal >= sem.deltaGlobalEsperado;
                    return (
                      <div style={{ background: '#fff', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 600, color: adelanto ? '#4ade80' : '#f87171', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Real avanzado</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: adelanto ? '#16a34a' : '#dc2626', lineHeight: 1 }}>
                          {sem.deltaGlobalReal >= 0 ? '+' : ''}{sem.deltaGlobalReal}%
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#94a3b8', marginTop: 4 }}>
                          {adelanto ? `+${Math.round((sem.deltaGlobalReal - sem.deltaGlobalEsperado) * 100) / 100}% sobre meta` : `${Math.round((sem.deltaGlobalReal - sem.deltaGlobalEsperado) * 100) / 100}% bajo meta`}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Partidas activas */}
                  <div style={{ background: '#fff', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Partidas activas</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{sem.totalActivas}</div>
                    <div style={{ fontSize: '0.71rem', color: '#94a3b8', marginTop: 4 }}>{sem.totalCompletadas} completadas</div>
                  </div>

                  {/* Partidas completadas % */}
                  <div style={{ background: '#fff', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Completitud</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                      {sem.totalActivas > 0 ? Math.round(sem.totalCompletadas / sem.totalActivas * 100) : 0}%
                    </div>
                    <div style={{ fontSize: '0.71rem', color: '#94a3b8', marginTop: 4 }}>partidas terminadas</div>
                  </div>
                </div>
              </div>

              {/* Tabla */}
              {sem.partidas.length > 0 ? (
                <table className="programa-tabla" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', paddingLeft: 16, fontSize: '0.7rem' }}>PARTIDA</th>
                      <th style={{ textAlign: 'left', paddingLeft: 8, fontSize: '0.7rem' }}>RUTA</th>
                      <th style={{ width: 110, textAlign: 'center', fontSize: '0.7rem' }}>SEMANA</th>
                      <th style={{ width: 90, textAlign: 'center', fontSize: '0.7rem' }}>META SEM.</th>
                      <th style={{ width: 90, textAlign: 'center', fontSize: '0.7rem' }}>ACTUAL</th>
                      <th style={{ width: 140, textAlign: 'center', fontSize: '0.7rem' }}>ESTADO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sem.partidas.map((p, pi) => {
                      const ok = !sem.esFutura && p.esperadoRef > 0 && p.actual >= p.esperadoRef;
                      const brecha = Math.round(p.brecha);
                      return (
                        <tr key={pi} className="programa-fila-actividad" style={{ background: p.completada ? '#f0fdf4' : undefined, opacity: p.completada ? 0.75 : 1 }}>
                          <td style={{ paddingLeft: 16, fontSize: '0.82rem', color: p.completada ? '#16a34a' : '#334155', textDecoration: p.completada ? 'line-through' : 'none' }}>
                            {p.arrastrada && !p.completada && (
                              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', marginRight: 6, background: '#dc2626', padding: '1px 6px', borderRadius: 99 }}>
                                ATRASADA
                              </span>
                            )}
                            {p.terminaEstaSemana && !p.completada && (
                              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', marginRight: 6, background: '#fee2e2', padding: '1px 6px', borderRadius: 99 }}>
                                VENCE
                              </span>
                            )}
                            {p.nombre}
                          </td>
                          <td style={{ paddingLeft: 8, fontSize: '0.75rem', color: '#64748b' }}>
                            {p.ruta || '—'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {p.origenSemana === 'anterior'
                              ? <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4b5563', background: '#f3f4f6', padding: '1px 7px', borderRadius: 99 }}>Sem. pasada</span>
                              : <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#111827', background: '#e5e7eb', padding: '1px 7px', borderRadius: 99 }}>Esta sem.</span>
                            }
                          </td>
                          <td style={{ textAlign: 'center', fontSize: '0.85rem', color: '#475569' }}>{p.esperadoFinSem}%</td>
                          <td style={{ textAlign: 'center', fontSize: '0.85rem', fontWeight: 700,
                            color: sem.esFutura ? '#94a3b8' : ok ? '#16a34a' : '#dc2626' }}>
                            {p.actual}%
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {p.completada
                              ? <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a' }}>✓ Completada</span>
                              : sem.esFutura || p.esperadoRef === 0
                                ? <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>pendiente</span>
                                : ok
                                  ? <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a' }}>✓ Excelente</span>
                                  : <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>▼ {brecha}%</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '10px 16px', fontSize: '0.82rem', color: '#9ca3af' }}>
                  Sin partidas activas esta semana.
                </div>
              )}
            </div>
          </div>
        );
      })()}

    </div>
  );
}
